import { memo, useCallback, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { FormSection, FormTextField, FormTextFieldInlineEdit } from '@common-components';
import { useFormProvider, useIsMobile, useProcessNewLabels } from 'hooks';
import useUpdateNodeFormData from 'pages/app-pages/chatbot-builder-page/hooks/useUpdateNodeFormData';
import FormLabelsSelector from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/FormLabelsSelector';
import NodesFormTextEditor from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/NodesFormTextEditor';
import StepFormFieldsWrapper from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormFieldsWrapper';
import StepFormSubmitButtons from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormSubmitButtons';
import StepFormWrapper from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/form-components/StepFormWrapper';
import { RequestLocationFormFields } from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/request-location/types';
import {
  defaultLocationDataFormValues,
  requestLocationFieldsConfig,
  requestLocationStepFormSchema,
} from 'pages/app-pages/chatbot-builder-page/nodes/components/steps/request-location/utils';

import { Mention } from 'types';
import { characterLimitValue } from 'utils';

interface RequestLocationProps {
  id: string;
  defaultValues?: Partial<RequestLocationFormFields>;
  defaultName: string;
  mentions?: Mention[];
  commands?: Mention[];
}

const RequestLocation = ({
  defaultValues,
  mentions,
  commands,
  id,
  defaultName,
}: RequestLocationProps) => {
  const [localDefaultValues, setLocalDefaultValues] = useState({
    ...defaultLocationDataFormValues,
    ...defaultValues,
    name: defaultValues?.name || defaultName,
    nodeId: defaultValues?.nodeId || uuid(),
    labels: defaultValues?.labels || defaultLocationDataFormValues.labels || [],
  });

  const onLocalDefaultValuesChange = useCallback(
    (newDefaultValues: Partial<RequestLocationFormFields>) => {
      setLocalDefaultValues((prev) => ({
        ...prev,
        ...newDefaultValues,
      }));
    },
    [],
  );

  const formMethods = useFormProvider<RequestLocationFormFields>({
    schema: requestLocationStepFormSchema,
    defaultValues: localDefaultValues,
  });

  const updateNodeFormData = useUpdateNodeFormData();

  const { isMobile } = useIsMobile();

  const localCommands = useRef(commands);
  const localMentions = useRef(mentions);

  const { reset } = formMethods;

  const processNewLabels = useProcessNewLabels();
  const onReset = () => {
    reset(localDefaultValues);
  };

  const handleOnSubmit = async (formData: RequestLocationFormFields) => {
    const labelIds = await processNewLabels(formData.labels);

    await updateNodeFormData({
      id,
      formData: {
        ...formData,
        labels: labelIds,
      },
    });
  };

  const { name, labels, button, input } = requestLocationFieldsConfig;

  return (
    <StepFormWrapper formMethods={formMethods} handleOnSubmit={handleOnSubmit}>
      <StepFormFieldsWrapper>
        <FormTextFieldInlineEdit {...name} variant='standard' size='small' />
        <FormLabelsSelector {...labels} onLocalDefaultValuesChange={onLocalDefaultValuesChange} />

        <NodesFormTextEditor
          characterLimit={characterLimitValue}
          autoFocus={!isMobile}
          {...input}
          mentions={localMentions.current}
          commands={localCommands.current}
        />
        <FormSection title='Action Button'>
          <FormTextField {...button} size='small' />
        </FormSection>
      </StepFormFieldsWrapper>
      <StepFormSubmitButtons formState={formMethods.formState} reset={onReset} />
    </StepFormWrapper>
  );
};

export default memo(RequestLocation);
